import { Handle, Position } from '@xyflow/react';
import { Badge, Flex, rem, Text, Tooltip } from '@mantine/core';
import React, { CSSProperties, PropsWithChildren } from 'react';
import ReactCountryFlag from 'react-country-flag';
import styled, { css } from 'styled-components';
import {
	NodeData,
	NodeLoanData,
} from '@dealstack/frontend/app/pages/Project/Cap/CapChartPage/CapChart/chart.model.ts';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme.ts';
import { MipGroupBadge } from '@dealstack/frontend/app/pages/Project/Cap/CapTablePage/common/tableElements.tsx';
import { makeComplexDataTestId, TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds.ts';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';

export const NODE_WIDTH = 250;
export const NODE_HEIGHT = 100;

const targetHandlePosition = Position.Bottom;
const sourceHandlePosition = Position.Top;
const sourceDebtHandlePosition = Position.Left;

const RegistrationBadge = ({ data }: PropsWithChildren<{ data: NodeData }>) => {
	const [countryCode] = (data.countryCode || '').split('-');
	const registeredId = data.registeredId || '';

	return (
		<Badge
			size="sm"
			variant="filled"
			color="gray.2"
			c="dark.4"
			sx={{ padding: '0.25rem 0.5rem', maxWidth: '200px' }}
		>
			<Tooltip label={registeredId} withArrow>
				<Flex gap="0.25rem" align="center" style={{ overflow: 'hidden', maxWidth: '100%' }}>
					{countryCode && (
						<ReactCountryFlag
							countryCode={countryCode}
							svg
							style={{
								width: '1em',
								height: '1em',
							}}
							title={countryCode}
						/>
					)}
					<Text
						component="h6"
						variant="p-md"
						fw={400}
						c={defaultTheme.v3.colors.dark[800]}
						style={{
							whiteSpace: 'nowrap',
							overflow: 'hidden',
							textOverflow: 'ellipsis',
							maxWidth: '100%',
						}}
					>
						{registeredId}
					</Text>
				</Flex>
			</Tooltip>
		</Badge>
	);
};

/** The Review step's role pill — "Target", or "New" for a company the merge creates — plus its conflict marker. */
const ReviewBadges = ({ data }: { data: NodeData }) => {
	const { tag, tagTooltip, conflictLabel } = data.review ?? {};
	const pill =
		tag === undefined ? null : (
			<Badge
				size="sm"
				variant="filled"
				color="gray.2"
				c="dark.4"
				sx={{ padding: '0.25rem 0.5rem', maxWidth: '200px' }}
			>
				<Text
					component="h6"
					variant="p-md"
					fw={400}
					c={defaultTheme.v3.colors.dark[800]}
					style={{ whiteSpace: 'nowrap' }}
				>
					{tag}
				</Text>
			</Badge>
		);
	return (
		<>
			{tagTooltip === undefined ? (
				pill
			) : (
				<Tooltip label={tagTooltip} withArrow>
					{pill}
				</Tooltip>
			)}
			{conflictLabel && (
				<Tooltip label={conflictLabel} withArrow>
					<StyledConflictMarker aria-label={conflictLabel}>
						<Icon variant="exclamation-circle" size="lg" />
					</StyledConflictMarker>
				</Tooltip>
			)}
		</>
	);
};

const RegistrationData = ({
	data,
}: PropsWithChildren<{
	data: NodeData;
}>) => {
	const hasBadges =
		data.registeredId ||
		data.countryCode ||
		data.isMipGroup ||
		data.review?.tag ||
		data.review?.conflictLabel;
	return (
		<Flex
			direction="column"
			justify="flex-end"
			align="center"
			className="registration-data-container"
			style={
				hasBadges
					? {
							height: '100%',
						}
					: {
							margin: 'auto',
						}
			}
			data-testid={makeComplexDataTestId(TEST_ELEMENT_IDS.GSC_NODE_DATA, data.registeredName)}
		>
			<Text
				component="h6"
				variant="B6Bold"
				fw={600}
				lineClamp={2}
				truncate
				title={data.registeredName}
				c={defaultTheme.v3.colors.dark[800]}
				style={{
					lineHeight: 1.2,
					textAlign: 'center',
				}}
			>
				{data.registeredName}
			</Text>
			{hasBadges && (
				<Flex
					gap="0.25rem"
					align="center"
					justify="center"
					wrap="wrap"
					// Review adds a pill and a marker beside the registration badge; at 150px the three wrap to a
					// second row, which a 100px-tall node has no space for.
					maw={data.review === undefined ? '150px' : '220px'}
					mt="1rem"
				>
					{data.registeredId || data.countryCode ? (
						<RegistrationBadge data={data} />
					) : null}
					{data.isMipGroup && <MipGroupBadge />}
					<ReviewBadges data={data} />
				</Flex>
			)}
		</Flex>
	);
};

const makeNodeHandles = (
	loanData?: NodeLoanData,
	styles?: {
		source?: React.CSSProperties;
		target?: React.CSSProperties;
		sourceDebtLeft?: React.CSSProperties;
		targetDebtLeft?: React.CSSProperties;
		sourceDebtRight?: React.CSSProperties;
		targetDebtRight?: React.CSSProperties;
	}
) => {
	return (
		<>
			<Handle
				type="target"
				id="target"
				style={styles?.target}
				position={targetHandlePosition}
			/>
			<Handle
				type="source"
				id="source"
				style={styles?.source}
				position={sourceHandlePosition}
			/>
			{loanData?.source ? (
				<Handle
					type="source"
					id="source-debt"
					style={{
						width: '12px',
						height: '12px',
						backgroundColor: '#d3d8da',
						...(loanData.sourcePosition === Position.Right
							? styles?.sourceDebtRight
							: styles?.sourceDebtLeft),
					}}
					position={loanData.sourcePosition || sourceDebtHandlePosition}
				/>
			) : null}
			{loanData?.target ? (
				<>
					<Handle
						type="target"
						id={`target-debt-${Position.Right}`}
						style={{
							...styles?.targetDebtRight,
						}}
						position={Position.Right}
					/>
					<Handle
						type="target"
						id={`target-debt-${Position.Left}`}
						style={{
							...styles?.targetDebtLeft,
						}}
						position={Position.Left}
					/>
				</>
			) : null}
		</>
	);
};

/** Every node shape's outer box; Review draws a company the merge creates dashed. */
const NodeShell = ({
	data,
	style,
	children,
}: PropsWithChildren<{ data: NodeData; style?: CSSProperties }>) => (
	<StyledNodeContainer style={style} $new={data.review?.isNew === true}>
		{children}
	</StyledNodeContainer>
);

const roundedPathPartnership =
	'M12.512,99A4,4,38.320113936777446,0,1,10.032,91.862L122.52,2.96A4,4,103.35977212644511,0,1,127.48,2.96L239.968,91.862A4,4,38.320113936777446,0,1,237.488,99Z';

export const PartnershipNode = ({ data }: { data: NodeData }) => {
	return (
		<NodeShell data={data}>
			{makeNodeHandles(data?.loanData, {
				source: { translate: '0px 2px' },
				target: { translate: '0px -2px' },
				sourceDebtLeft: { transform: `translate(50px, 0px)` },
				targetDebtLeft: { transform: `translate(63px, 0px)` },
				sourceDebtRight: { transform: `translate(-50px, 0px)` },
				targetDebtRight: { transform: `translate(-63px, 0px)` },
			})}
			<svg xmlns="http://www.w3.org/2000/svg" width={NODE_WIDTH} height={NODE_HEIGHT}>
				<g>
					<path
						d={roundedPathPartnership}
						id="partnership-node"
						fill="#F4F5F6"
						stroke="#D3D8DA"
					/>
				</g>
			</svg>
			<StyledAbsoluteNodeContainer>
				<RegistrationData data={data} />
			</StyledAbsoluteNodeContainer>
		</NodeShell>
	);
};

const roundedPathTrust =
	'M2.507 41.627a3.143 3.143 82.727 0 1 1.598 -4.439L97.059 0.457a3.143 3.143 136.876 0 1 2.31 0L192.324 37.188a3.143 3.143 82.727 0 1 1.598 4.439l-35.883 65.174a3.143 3.143 -241.164 0 1 -2.753 1.627H41.143a3.143 3.143 -241.164 0 1 -2.753 -1.627Z';

export const TrustNode = ({ data }: { data: NodeData }) => {
	return (
		<NodeShell data={data}>
			{makeNodeHandles(data?.loanData, {
				source: { translate: '0px 1px' },
				target: { translate: '0px 2px' },
				sourceDebtLeft: { transform: 'translate(24px, -16px)' },
				targetDebtLeft: { transform: 'translate(30px, -11px)' },
				sourceDebtRight: { transform: 'translate(-24px, -16px)' },
				targetDebtRight: { transform: 'translate(-30px, -12px)' },
			})}
			<svg width={196.429} height={110}>
				<g>
					<path d={roundedPathTrust} fill="#F4F5F6" stroke="#D3D8DA" />
				</g>
			</svg>
			<StyledAbsoluteNodeContainer style={{ height: 'calc(100% + 8px)' }}>
				<RegistrationData data={data} />
			</StyledAbsoluteNodeContainer>
		</NodeShell>
	);
};

const StyledSharedBaseShapeContainer = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	padding: ${rem('8px')};
	width: ${NODE_WIDTH}px;
	height: ${NODE_HEIGHT}px;
`;

const StyledSharedBaseShape = styled(StyledSharedBaseShapeContainer)`
	background-color: ${({ theme }) => theme.v3.colors.gray[0]};
	border: 1px solid ${({ theme }) => theme.v3.colors.gray[4]};
`;

const StyledEllipse = styled(StyledSharedBaseShape)`
	border-radius: 50%;
`;

export const OtherNode = ({ data }: { data: NodeData }) => {
	return (
		<NodeShell data={data}>
			{makeNodeHandles(data?.loanData)}
			<StyledEllipse>
				<RegistrationData data={data} />
			</StyledEllipse>
		</NodeShell>
	);
};

const StyledRectangle = styled(StyledSharedBaseShape)`
	border-radius: 0.25rem;
`;

export const CompanyNode = ({ data }: { data: NodeData }) => {
	return (
		<NodeShell data={data} style={{ alignItems: 'start' }}>
			{makeNodeHandles(data?.loanData)}
			<StyledRectangle>
				<RegistrationData data={data} />
			</StyledRectangle>
		</NodeShell>
	);
};

const StyledNodeContainer = styled.div<{ $new?: boolean }>`
	height: 100%;

	/* A company the merge creates is drawn dashed, whichever shape it takes: CSS border or SVG stroke. */
	${({ $new, theme }) =>
		$new &&
		css`
			${StyledSharedBaseShape} {
				border-style: dashed;
				border-color: ${theme.v3.colors.blue[6]};
			}

			svg path {
				stroke-dasharray: 5 3;
				stroke: ${theme.v3.colors.blue[6]};
			}
		`}
`;

/** Sits in the badge row where the merge would take a holding below zero. */
const StyledConflictMarker = styled.span`
	display: flex;
	color: ${({ theme }) => theme.v3.colors.red[8]};
`;

const StyledAbsoluteNodeContainer = styled(StyledSharedBaseShapeContainer)`
	position: absolute;
	top: 0;
	left: 0;
	height: 100%;
`;
